import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../home/components/Footer';

interface SecurityUpdate {
  id: string;
  title: string;
  type: 'security' | 'update' | 'announcement';
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  date: string;
  description: string;
  affectedProducts: string[];
  status: 'resolved' | 'investigating' | 'monitoring';
  cveId?: string;
}

export default function SecurityPage() {
  const [activeTab, setActiveTab] = useState<'updates' | 'announcements'>('updates');

  const securityUpdates: SecurityUpdate[] = [
    {
      id: 'SEC-2025-001',
      title: 'Adobe PDF Library SDK 安全补丁 v2.1.3',
      type: 'security',
      severity: 'high',
      date: '2025-01-15',
      description: '修复了PDF解析器中的潜在缓冲区溢出漏洞，建议所有用户立即更新到最新版本。',
      affectedProducts: ['Adobe PDF Library SDK'],
      status: 'resolved',
      cveId: 'CVE-2025-0001'
    },
    {
      id: 'SEC-2025-002',
      title: 'PDF表单扩展SDK 权限验证增强',
      type: 'security',
      severity: 'medium',
      date: '2025-01-12',
      description: '增强了表单字段访问权限验证机制，防止未授权的表单数据修改。',
      affectedProducts: ['PDF表单扩展SDK'],
      status: 'resolved'
    },
    {
      id: 'UPD-2025-003',
      title: 'API网关安全协议升级',
      type: 'update',
      severity: 'medium',
      date: '2025-01-10',
      description: '升级TLS协议到1.3版本，提升数据传输安全性，停止支持过时的加密算法。',
      affectedProducts: ['API网关', 'PDF REST Container'],
      status: 'resolved'
    },
    {
      id: 'SEC-2025-004',
      title: '电子签署服务数字证书更新',
      type: 'security',
      severity: 'low',
      date: '2025-01-08',
      description: '更新了CA根证书链，确保数字签名的长期有效性和法律效力。',
      affectedProducts: ['电子签署'],
      status: 'resolved'
    },
    {
      id: 'UPD-2025-005',
      title: 'OCR文本识别安全优化',
      type: 'update',
      severity: 'low',
      date: '2025-01-05',
      description: '优化了图像处理流程，增加了恶意文件检测机制，提升服务安全性。',
      affectedProducts: ['OCR文本识别'],
      status: 'resolved'
    }
  ];

  const securityAnnouncements: SecurityUpdate[] = [
    {
      id: 'ANN-2025-001',
      title: '2025年第一季度安全审计完成',
      type: 'announcement',
      severity: 'info',
      date: '2025-01-20',
      description: '我们已完成2025年第一季度的全面安全审计，所有系统均通过安全评估。审计报告已提交给相关监管机构。',
      affectedProducts: ['所有产品'],
      status: 'resolved'
    },
    {
      id: 'ANN-2025-002',
      title: '新增ISO 27001认证',
      type: 'announcement',
      severity: 'info',
      date: '2025-01-18',
      description: '星允信息已获得ISO 27001:2022信息安全管理体系认证，进一步确保客户数据安全。',
      affectedProducts: ['所有产品'],
      status: 'resolved'
    },
    {
      id: 'ANN-2025-003',
      title: '安全漏洞奖励计划启动',
      type: 'announcement',
      severity: 'info',
      date: '2025-01-15',
      description: '我们正式启动安全漏洞奖励计划，欢迎安全研究人员报告发现的安全问题。奖励金额最高可达50,000元。',
      affectedProducts: ['所有产品'],
      status: 'resolved'
    },
    {
      id: 'ANN-2025-004',
      title: '数据中心安全升级完成',
      type: 'announcement',
      severity: 'info',
      date: '2025-01-10',
      description: '我们的主要数据中心已完成物理安全设施升级，包括生物识别访问控制和24/7安全监控。',
      affectedProducts: ['基础设施'],
      status: 'resolved'
    }
  ];

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'text-red-700 bg-red-100 border-red-200';
      case 'high':
        return 'text-orange-700 bg-orange-100 border-orange-200';
      case 'medium':
        return 'text-yellow-700 bg-yellow-100 border-yellow-200';
      case 'low':
        return 'text-blue-700 bg-blue-100 border-blue-200';
      case 'info':
        return 'text-green-700 bg-green-100 border-green-200';
      default:
        return 'text-gray-700 bg-gray-100 border-gray-200';
    }
  };

  const getSeverityText = (severity: string) => {
    switch (severity) {
      case 'critical':
        return '严重';
      case 'high':
        return '高';
      case 'medium':
        return '中等';
      case 'low':
        return '低';
      case 'info':
        return '信息';
      default:
        return '未知';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'security':
        return 'ri-shield-check-line';
      case 'update':
        return 'ri-refresh-line';
      case 'announcement':
        return 'ri-megaphone-line';
      default:
        return 'ri-information-line';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'resolved':
        return 'text-green-600 bg-green-100';
      case 'investigating':
        return 'text-yellow-600 bg-yellow-100';
      case 'monitoring':
        return 'text-blue-600 bg-blue-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'resolved':
        return '已解决';
      case 'investigating':
        return '调查中';
      case 'monitoring':
        return '监控中';
      default:
        return '未知';
    }
  };

  const currentData = activeTab === 'updates' ? securityUpdates : securityAnnouncements;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-600 via-cyan-600 to-teal-700 text-white py-24 pt-32 overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjEiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-20"></div>
        
        {/* Animated Magnifying Glass */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 pointer-events-none">
          {/* Scanning area highlight */}
          <div className="absolute inset-0 animate-pulse">
            <div className="absolute top-1/4 left-1/4 w-32 h-32 border-2 border-yellow-400/30 rounded-lg animate-[scan_3s_ease-in-out_infinite]"></div>
            <div className="absolute top-1/3 right-1/4 w-24 h-24 border-2 border-green-400/30 rounded-lg animate-[scan_4s_ease-in-out_infinite_0.5s]"></div>
            <div className="absolute bottom-1/3 left-1/3 w-28 h-28 border-2 border-blue-400/30 rounded-lg animate-[scan_3.5s_ease-in-out_infinite_1s]"></div>
          </div>
          
          {/* Magnifying Glass */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-[search_4s_ease-in-out_infinite]">
            {/* Glass circle */}
            <div className="relative w-32 h-32">
              <div className="absolute inset-0 rounded-full border-4 border-white/40 bg-white/10 backdrop-blur-sm"></div>
              <div className="absolute inset-2 rounded-full border-2 border-white/60"></div>
              {/* Glare effect */}
              <div className="absolute top-4 left-4 w-8 h-8 rounded-full bg-white/40 blur-sm"></div>
              {/* Scanning line */}
              <div className="absolute inset-0 overflow-hidden rounded-full">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent animate-[scanLine_2s_linear_infinite]"></div>
              </div>
            </div>
            {/* Handle */}
            <div className="absolute -bottom-2 -right-2 w-16 h-2 bg-white/40 rounded-full origin-left rotate-45"></div>
            <div className="absolute -bottom-4 -right-4 w-12 h-2 bg-white/40 rounded-full origin-left rotate-45"></div>
          </div>
          
          {/* Scanning particles */}
          <div className="absolute inset-0">
            <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-teal-400 rounded-full animate-[particle_3s_ease-in-out_infinite]"></div>
            <div className="absolute top-1/3 right-1/3 w-2 h-2 bg-cyan-400 rounded-full animate-[particle_3s_ease-in-out_infinite_0.5s]"></div>
            <div className="absolute bottom-1/3 left-1/2 w-2 h-2 bg-teal-300 rounded-full animate-[particle_3s_ease-in-out_infinite_1s]"></div>
            <div className="absolute top-1/2 right-1/4 w-2 h-2 bg-cyan-300 rounded-full animate-[particle_3s_ease-in-out_infinite_1.5s]"></div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/10 rounded-full mb-6 backdrop-blur-sm">
              <i className="ri-shield-check-line text-4xl"></i>
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">
              安全中心
            </h1>
            <p className="text-xl lg:text-2xl mb-8 max-w-3xl mx-auto text-teal-100">
              我们致力于保护您的数据安全，持续监控和改进我们的安全措施
            </p>
          </div>
        </div>

        {/* Add custom animations */}
        <style>{`
          @keyframes search {
            0%, 100% { transform: translate(-50%, -50%) translate(0, 0) rotate(0deg); }
            25% { transform: translate(-50%, -50%) translate(40px, -30px) rotate(5deg); }
            50% { transform: translate(-50%, -50%) translate(-30px, 40px) rotate(-5deg); }
            75% { transform: translate(-50%, -50%) translate(30px, 30px) rotate(3deg); }
          }
          
          @keyframes scan {
            0%, 100% { opacity: 0.3; transform: scale(1); }
            50% { opacity: 0.8; transform: scale(1.1); }
          }
          
          @keyframes scanLine {
            0% { transform: translateY(0); }
            100% { transform: translateY(128px); }
          }
          
          @keyframes particle {
            0%, 100% { opacity: 0; transform: scale(0); }
            50% { opacity: 1; transform: scale(1.5); }
          }
        `}</style>
      </section>

      {/* Security Stats */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-md text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-fill text-3xl text-green-600"></i>
              </div>
              <p className="text-3xl font-bold text-gray-900 mb-2">ISO 27001</p>
              <p className="text-gray-600">信息安全认证</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-3xl text-blue-600"></i>
              </div>
              <p className="text-3xl font-bold text-gray-900 mb-2">24/7</p>
              <p className="text-gray-600">安全监控</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-bug-line text-3xl text-purple-600"></i>
              </div>
              <p className="text-3xl font-bold text-gray-900 mb-2">0</p>
              <p className="text-gray-600">未解决漏洞</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-award-line text-3xl text-orange-600"></i>
              </div>
              <p className="text-3xl font-bold text-gray-900 mb-2">99.9%</p>
              <p className="text-gray-600">安全可用性</p>
            </div>
          </div>
        </div>
      </section>

      {/* Security Updates and Announcements */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">安全更新与公告</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              及时了解我们的安全更新、漏洞修复和重要安全公告
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="flex justify-center mb-8">
            <div className="bg-gray-100 rounded-full p-1 flex">
              <button
                onClick={() => setActiveTab('updates')}
                className={`px-6 py-2 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === 'updates'
                    ? 'bg-teal-600 text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                安全更新
              </button>
              <button
                onClick={() => setActiveTab('announcements')}
                className={`px-6 py-2 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === 'announcements'
                    ? 'bg-teal-600 text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                安全公告
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-6">
            {currentData.map((item) => (
              <div key={item.id} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className={`${getTypeIcon(item.type)} text-2xl text-teal-600`}></i>
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-start justify-between flex-wrap gap-4 mb-3">
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getSeverityColor(item.severity)}`}>
                            {getSeverityText(item.severity)}
                          </span>
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(item.status)}`}>
                            {getStatusText(item.status)}
                          </span>
                          {item.cveId && (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold text-red-700 bg-red-100">
                              {item.cveId}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500 mb-1">发布日期</p>
                        <p className="text-sm font-semibold text-gray-900">{item.date}</p>
                      </div>
                    </div>

                    <p className="text-gray-700 mb-4 leading-relaxed">{item.description}</p>

                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div>
                        <p className="text-sm text-gray-500 mb-1">影响产品</p>
                        <div className="flex flex-wrap gap-2">
                          {item.affectedProducts.map((product) => (
                            <span key={product} className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700">
                              {product}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">ID:</span>
                        <span className="text-sm font-mono text-gray-900">{item.id}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Best Practices */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">安全最佳实践</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              遵循这些安全建议，确保您的系统和数据安全
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-refresh-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">定期更新</h3>
              <p className="text-gray-600">
                及时安装安全补丁和版本更新，确保使用最新的安全功能。
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-key-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">强密码策略</h3>
              <p className="text-gray-600">
                使用复杂密码，启用双因素认证，定期更换API密钥。
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-eye-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">监控日志</h3>
              <p className="text-gray-600">
                定期检查访问日志和系统日志，及时发现异常活动。
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-shield-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">网络安全</h3>
              <p className="text-gray-600">
                使用HTTPS连接，配置防火墙，限制不必要的网络访问。
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-database-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">数据备份</h3>
              <p className="text-gray-600">
                定期备份重要数据，测试恢复流程，确保业务连续性。
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-team-line text-2xl text-yellow-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">员工培训</h3>
              <p className="text-gray-600">
                定期进行安全意识培训，提高团队的安全防范能力。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Security Team */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">发现安全问题？</h2>
          <p className="text-gray-600 mb-8">
            如果您发现了安全漏洞或有安全相关的问题，请立即联系我们的安全团队
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-teal-50 rounded-xl p-6">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">安全邮箱</h3>
              <p className="text-teal-600 font-semibold">security@documogul.com</p>
              <p className="text-sm text-gray-600 mt-2">24小时内响应</p>
            </div>

            <div className="bg-green-50 rounded-xl p-6">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-award-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">漏洞奖励</h3>
              <p className="text-green-600 font-semibold">最高 ¥50,000</p>
              <p className="text-sm text-gray-600 mt-2">根据严重程度评估</p>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
            <div className="flex items-start gap-3">
              <i className="ri-information-line text-2xl text-yellow-600 flex-shrink-0 mt-1"></i>
              <div className="text-left">
                <h4 className="font-semibold text-gray-900 mb-2">负责任的披露政策</h4>
                <p className="text-gray-700 text-sm">
                  我们承诺在收到安全报告后24小时内确认，并在合理时间内修复问题。
                  请在我们修复问题之前不要公开披露漏洞详情。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}